import { PROSPECT_NOTES_MAX_LENGTH, ProspectListNameOrder, type ProspectListPreviewResponse } from '@signalvote/shared';
import { Input } from '@/components/ui/input';
import { PendingButton } from '@/components/ui/pending-button';
import { gateLines } from '@/lib/fundraising/prospectListUpload';

const count = (n: number) => n.toLocaleString();

const NAME_ORDER_LABELS: Record<ProspectListNameOrder, string> = {
  [ProspectListNameOrder.FirstLast]: 'First Last (John Smith)',
  [ProspectListNameOrder.LastFirst]: 'Last, First (Smith, John)',
};

/**
 * The confirmation gate: counts over every row of the file, how names will be split, and the one button
 * that adds the people and matches them to federal giving. A wrong column writes thousands of wrong
 * people, so this is where a person catches it. Presentational — the container supplies the preview and
 * the actions; `checking` holds the button while a check for the current choices is still out.
 */
export function ProspectListGate({
  preview,
  listName,
  onListNameChange,
  onNameOrderChange,
  onConfirm,
  isConfirming = false,
  checking = false,
  confirmError,
}: {
  preview: ProspectListPreviewResponse;
  listName: string;
  onListNameChange: (name: string) => void;
  onNameOrderChange: (order: ProspectListNameOrder) => void;
  onConfirm: () => void;
  isConfirming?: boolean;
  checking?: boolean;
  confirmError?: string;
}) {
  return (
    <section className="space-y-4">
      <p className="text-sm text-foreground">
        {count(preview.totalRows)} rows in the file. These counts cover every row, so check them before you add anyone.
      </p>

      <ul className="divide-y rounded-md border text-sm">
        {gateLines(preview).map((line) => (
          <li key={line.label} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
            <span className="text-foreground">{line.label}</span>
            <span className={`tabular-nums ${line.low ? 'font-medium text-amber-700' : 'text-muted-foreground'}`}>
              {count(line.count)} of {count(preview.totalRows)} ({line.percent}%)
              {line.low && <span className="ml-2">Check which column you chose.</span>}
            </span>
          </li>
        ))}
      </ul>

      {preview.notesShortened > 0 && (
        <p className="text-sm text-muted-foreground">
          {count(preview.notesShortened)} {preview.notesShortened === 1 ? 'note is' : 'notes are'} longer than{' '}
          {count(PROSPECT_NOTES_MAX_LENGTH)} characters and will be shortened. Those people still come in.
        </p>
      )}

      {preview.nameOrder !== null && (
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground">Names are written</span>
          <select
            className="rounded-md border px-2 py-1 text-sm"
            value={preview.nameOrder}
            onChange={(e) => onNameOrderChange(e.target.value as ProspectListNameOrder)}
          >
            {Object.values(ProspectListNameOrder).map((order) => (
              <option key={order} value={order}>
                {NAME_ORDER_LABELS[order]}
              </option>
            ))}
          </select>
        </label>
      )}

      <table className="w-full text-left text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr>
            <th className="py-1 font-medium">In the file</th>
            <th className="py-1 font-medium">First name</th>
            <th className="py-1 font-medium">Last name</th>
          </tr>
        </thead>
        <tbody>
          {preview.sampleNames.map((name, i) => (
            <tr key={i} className="border-t">
              <td className="py-1 pr-2">{name.source}</td>
              {name.firstName !== null && name.lastName !== null ? (
                <>
                  <td className="py-1 pr-2">{name.firstName}</td>
                  <td className="py-1">{name.lastName}</td>
                </>
              ) : (
                <td colSpan={2} className="py-1 text-amber-700">
                  Not a usable name
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-[16rem] flex-1 space-y-1 text-sm">
          <span className="text-foreground">List name</span>
          <Input value={listName} onChange={(e) => onListNameChange(e.target.value)} maxLength={200} />
        </label>
        <PendingButton pending={isConfirming} disabled={checking || preview.rowsWithUsableName === 0} onClick={onConfirm}>
          {isConfirming
            ? 'Starting…'
            : `Add ${count(preview.rowsWithUsableName)} ${preview.rowsWithUsableName === 1 ? 'person' : 'people'} and match them to federal giving`}
        </PendingButton>
      </div>
      {confirmError && <p className="text-sm text-amber-700">{confirmError}</p>}
    </section>
  );
}
